import { useState } from "react";
import { ChevronDown, ChevronRight, Wrench } from "lucide-react";
import * as Icons from "lucide-react";
import { MaturityBadge, NatureBadge } from "@/components/Badges";
import { t } from "@/lib/i18n";

const SubNode = ({ sub, selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const active = selected === sub.id;
  return (
    <div className="border-l border-border ml-3 pl-3">
      <div className={`flex items-start gap-2 rounded-md px-2 py-1.5 -ml-2 cursor-pointer transition-colors ${active ? "bg-accent" : "hover:bg-accent/60"}`}>
        <button data-testid={`sub-toggle-${sub.id}`} onClick={() => setOpen(!open)} className="mt-0.5 text-muted-foreground">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</button>
        <button data-testid={`sub-node-${sub.id}`} onClick={() => onSelect(sub.id)} className="flex-1 text-left">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="num text-[10px] text-muted-foreground">{sub.code}</span>
            <span className="text-sm font-medium">{sub.title}</span>
            {sub.tool && <Wrench size={12} className="text-primary" />}
          </div>
          <div className="flex gap-1.5 mt-1 flex-wrap"><MaturityBadge maturity={sub.maturity} /><NatureBadge nature={sub.nature} /></div>
        </button>
      </div>
      {open && (
        <ul className="ml-4 mt-1 mb-2 space-y-0.5">
          {sub.children.map((c) => (
            <li key={c.id}>
              <button data-testid={`concept-node-${c.id}`} onClick={() => onSelect(c.id)}
                className={`text-left text-xs px-2 py-1 rounded w-full transition-colors ${selected === c.id ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-accent/60"}`}>• {c.title}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const BranchCard = ({ branch, selected, onSelect, maturity, index }) => {
  const [open, setOpen] = useState(index === 0);
  const Icon = Icons[branch.icon] || Icons.Circle;
  const subs = branch.children.filter((s) => !maturity || s.maturity === maturity);
  return (
    <div data-testid={`branch-card-${branch.id}`} className="surface overflow-hidden fade-up" style={{ animationDelay: `${index * 60}ms`, borderColor: selected === branch.id ? branch.color : undefined }}>
      <div className="flex items-stretch">
        <span className="w-1.5 shrink-0" style={{ background: branch.color }} />
        <button data-testid={`branch-header-${branch.id}`} onClick={() => { setOpen(!open); onSelect(branch.id); }} className="flex-1 flex items-center gap-3 p-4 text-left">
          <span className="h-9 w-9 rounded-md flex items-center justify-center shrink-0" style={{ background: `${branch.color}22`, color: branch.color }}><Icon size={18} /></span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2"><span className="num text-xs text-muted-foreground">{branch.code}</span><span className="font-heading font-semibold truncate">{branch.title}</span></div>
            <div className="text-xs text-muted-foreground truncate">{branch.subtitle}</div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <MaturityBadge maturity={branch.maturity} />
            <span className="text-[10px] text-muted-foreground">{branch.children.length} sotto-rami · {branch.files.length} file</span>
          </div>
        </button>
      </div>
      {open && (
        <div className="px-3 pb-3 space-y-1">
          {subs.length === 0 && <div className="text-xs text-muted-foreground px-3 py-2">{t("Nessun sotto-ramo con questa maturità.")}</div>}
          {subs.map((s) => <SubNode key={s.id} sub={s} selected={selected} onSelect={onSelect} />)}
        </div>
      )}
    </div>
  );
};

export const BranchTree = ({ tree, selected, onSelect, maturity }) => (
  <div data-testid="branch-tree" className="space-y-3">
    {tree.map((b, i) => <BranchCard key={b.id} branch={b} index={i} selected={selected} onSelect={onSelect} maturity={maturity} />)}
  </div>
);
